import calendarTypes from "../../../../types/calendarTypes";
import Precision from "../../../../types/datePrecision";
import floatToInt from "../../../../utils/floatToInt";
import { getEraIsBackwards } from "../selectors";
import dateByAddingSpan from "./dateByAddingSpan";
import {
  calculateTimestampForDate,
  calculateWeekdayForTimestamp,
  createImmutableDate,
  getFirstMonthOfYear
} from "./dateInternalCalculations";
import { getDaysSinceZeroDate, getHoursSinceZeroDate, getWeeksSinceZeroDate } from "./stringFromZeroDate";
import { timespanForComponents } from "./timespanFunctions";
// ===================================================
// Exported Functions
// ===================================================

const dateAtEvenSpanBeforeDate = (calendar, date, span) => {
  // TODO:   Should use getCalendarTypeFromCalendar(calendar) once we have the other bits setup...?

  const isAbsoluteCalendar = calendar.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME;

  if (isAbsoluteCalendar) {
    return dateAtEvenSpanBeforeAbsoluteDate(calendar, date, span);
  } else {
    return dateAtEvenSpanBeforeRelativeDate(calendar, date, span);
  }
};

export default dateAtEvenSpanBeforeDate;

// ===================================================
// Internal Functions
// ===================================================

const dateAtEvenSpanBeforeAbsoluteDate = (calendar, date, span) => {
  const spanYears = span.years;
  const spanMonths = span.months;
  const spanWeeks = span.weeks;
  const spanDays = span.days;
  const spanHours = span.hours;
  const spanMinutes = span.minutes;
  const spanSeconds = span.seconds;
  let era = date.era;
  let year = date.year;
  let month = date.month;
  let day = date.day;
  let hour = date.hour;
  let minute = date.minute;
  let second = date.second;
  let timestamp = date.timestamp;
  let weekday = date.weekday;
  let precision = date.precision;

  // Weeks are treated slightly differently
  if (spanWeeks > 0) {
    const dateWeeks = getWeeksSinceZeroDate(calendar, date);
    const weeks = (dateWeeks > 0 ? dateWeeks - 1 : dateWeeks) % spanWeeks;
    // go back to start of week
    const adjustSpan = timespanForComponents(0, 0, -weeks, -weekday, -hour, -minute, -second);
    return dateByAddingSpan(calendar, date, adjustSpan).set("precision", Precision.Week);
  }

  // Otherwise, take a copy of our date and edit as needed.
  if (spanSeconds > 0) {
    second = floatToInt(second / spanSeconds) * spanSeconds;
    precision = Precision.Second;
  } else if (spanMinutes > 0) {
    minute = floatToInt(minute / spanMinutes) * spanMinutes;
    second = 0;
    precision = Precision.Minute;
  } else if (spanHours > 0) {
    hour = floatToInt(hour / spanHours) * spanHours;
    minute = 0;
    second = 0;
    precision = Precision.Hour;
  } else if (spanDays > 0) {
    day = floatToInt((day - 1) / spanDays) * spanDays + 1;
    hour = 0;
    minute = 0;
    second = 0;
    precision = Precision.Day;
  } else if (spanMonths > 0) {
    month = floatToInt((month - 1) / spanMonths) * spanMonths + 1;
    day = 1;
    hour = 0;
    minute = 0;
    second = 0;
    precision = Precision.Month;
  } else if (spanYears > 0) {
    if (getEraIsBackwards(calendar, era)) {
      year = Math.ceil(year / spanYears) * spanYears;
    } else {
      year = floatToInt((year - 1) / spanYears) * spanYears + 1;
    }
    month = getFirstMonthOfYear(calendar, era, year);
    day = 1;
    hour = 0;
    minute = 0;
    second = 0;
    precision = Precision.Year;
  }

  timestamp = calculateTimestampForDate(calendar, era, year, month, day, hour, minute, second);
  weekday = calculateWeekdayForTimestamp(calendar, timestamp);

  return createImmutableDate(era, year, month, day, hour, minute, second, timestamp, weekday, precision);
};

const dateAtEvenSpanBeforeRelativeDate = (calendar, date, span) => {
  const spanWeeks = span.weeks;
  const spanDays = span.days;
  const spanHours = span.hours;
  const spanMinutes = span.minutes;
  const spanSeconds = span.seconds;
  let era = date.era;
  let year = date.year;
  let month = date.month;
  let day = date.day;
  let hour = date.hour;
  let minute = date.minute;
  let second = date.second;
  let timestamp = date.timestamp;
  let weekday = date.weekday;
  let precision = date.precision;

  const calendarType = calendar.calendarType;

  // Weeks are treated slightly differently
  if (spanWeeks > 0) {
    const dateWeeks = getWeeksSinceZeroDate(calendar, date);
    const weeks = dateWeeks > 0 ? (dateWeeks - 1) % spanWeeks : ((spanWeeks + (dateWeeks % spanWeeks)) % spanWeeks) - 1;
    // go back to start of week
    const adjustSpan = timespanForComponents(0, 0, -weeks, -weekday, -hour, -minute, -second);
    return dateByAddingSpan(calendar, date, adjustSpan).set("precision", Precision.Week);
  }

  // Otherwise, take a copy of our date and edit as needed.
  if (spanSeconds > 0) {
    second = floatToInt(second / spanSeconds) * spanSeconds;
    precision = Precision.Second;
  } else if (spanMinutes > 0) {
    minute = floatToInt(minute / spanMinutes) * spanMinutes;
    second = 0;
    precision = Precision.Minute;
  } else if (spanHours > 0) {
    if (calendarType === calendarTypes.CALENDAR_TYPE_HOUR_TIME) {
      const roundedDate = dateByAddingSpan(calendar, date, timespanForComponents(0, 0, 0, 0, 0, -minute, -second)).set(
        "precision",
        Precision.Hour
      );
      const hoursSinceZero = getHoursSinceZeroDate(calendar, roundedDate);
      const isMinusDate = roundedDate.timestamp - calendar.zeroDateTimestamp < 0;
      const hourDiff = hoursSinceZero % spanHours;
      const hours = Math.abs(isMinusDate && hourDiff !== 0 ? hourDiff + spanHours : hourDiff);
      const result = dateByAddingSpan(calendar, roundedDate, timespanForComponents(0, 0, 0, 0, -hours, 0, 0)).set(
        "precision",
        Precision.Hour
      );
      return result;
    } else {
      hour = floatToInt(hour / spanHours) * spanHours;
      minute = 0;
      second = 0;
      precision = Precision.Hour;
    }
  } else if (spanDays > 0) {
    if (calendarType === calendarTypes.CALENDAR_TYPE_DAY_TIME) {
      const daysSinceZero = getDaysSinceZeroDate(calendar, date);
      const days =
        daysSinceZero > 0 ? (daysSinceZero - 1) % spanDays : ((spanDays + (daysSinceZero % spanDays)) % spanDays) - 1;

      return dateByAddingSpan(calendar, date, timespanForComponents(0, 0, 0, -days, -hour, -minute, -second)).set(
        "precision",
        Precision.Day
      );
    } else {
      day = floatToInt((day - 1) / spanDays) * spanDays + 1;
      hour = 0;
      minute = 0;
      second = 0;
      precision = Precision.Day;
    }
  }

  timestamp = calculateTimestampForDate(calendar, era, year, month, day, hour, minute, second);
  weekday = calculateWeekdayForTimestamp(calendar, timestamp);

  return createImmutableDate(era, year, month, day, hour, minute, second, timestamp, weekday, precision);
};
